import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import useAuthorizedRoles from '../shared/hooks/useAuthorizedRoles';
import { UserRole } from '../shared/ts/User';
import useCatalog, { errorMessage } from './useCatalog';
import { Catalog, Category, Discount, Service, startingPrice } from './pricing';
import { Action, Check, Columns, Message, Page, Panel } from './styles';

const newService = (): Service => ({
	id: 0,
	name: '',
	description: '',
	priceCents: 0,
	categoryId: 0,
	parentId: null,
	includedIds: [],
	discounts: [],
});

const moneyValue = (value: string) => Math.round(Number(value) * 100);

export default function AdminServices() {
	const user = useAuthorizedRoles([UserRole.ADMIN]);

	// Do not mount management or fetch data until the existing authorization check succeeds.
	return user?.role === UserRole.ADMIN ? <Management /> : null;
}

function Management() {
	const { catalog, loading, error, reload } = useCatalog();
	const [draft, setDraft] = useState<Service | null>(null);
	const [category, setCategory] = useState<Category>({ id: 0, name: '' });
	const [busy, setBusy] = useState(false);
	const [feedback, setFeedback] = useState('');
	const [failure, setFailure] = useState('');

	const mutate = async (work: () => Promise<any>, done?: () => void) => {
		if (busy) return;

		setBusy(true);
		setFeedback('');
		setFailure('');

		try {
			await work();
			done?.();
			await reload();
			setFeedback('השינויים נשמרו בהצלחה.');
		} catch (e) {
			setFailure(errorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const deleteService = (service: Service) => {
		const children = catalog.services.filter((s) => s.parentId === service.id);
		const includers = catalog.services.filter((s) =>
			s.includedIds.includes(service.id)
		);
		const discounts = catalog.services.filter((s) =>
			s.discounts.some((d) => d.targetIds.includes(service.id))
		);
		const message = `למחוק את השירות ״${service.name}״?\n${
			children.length
				? `שירותים שיוצגו ללא הורה: ${children
						.map((s) => s.name)
						.join(', ')}.\n`
				: ''
		}${
			includers.length
				? `השירות יוסר מהכללות אצל: ${includers
						.map((s) => s.name)
						.join(', ')}.\n`
				: ''
		}${
			discounts.length
				? `השירות יוסר מיעדי ההנחות אצל: ${discounts
						.map((s) => s.name)
						.join(', ')}. הנחות ללא יעד יימחקו.\n`
				: ''
		}כללי ההנחה של השירות יימחקו. לא ניתן לבטל מחיקה.`;

		if (window.confirm(message))
			mutate(
				() => axios.delete(`/services-management/services/${service.id}`),
				() => {
					if (draft?.id === service.id) setDraft(null);
				}
			);
	};

	const saveService = (e: React.FormEvent) => {
		e.preventDefault();

		if (!draft) return;

		const { id, ...body } = draft;
		const payload = {
			...body,
			discounts: body.discounts.map(({ id: ruleId, ...rule }) => rule),
		};
		mutate(
			() =>
				id
					? axios.put(`/services-management/services/${id}`, payload)
					: axios.post('/services-management/services', payload),
			() => setDraft(null)
		);
	};

	return (
		<Page>
			<h1>ניהול השירותים שלנו</h1>
			<p>
				<Link to="/services">לעמוד הציבורי</Link>
			</p>
			<p>
				צרו קטגוריות ולאחר מכן הוסיפו שירותים עם תיאור ומחיר התחלתי. קטגוריות
				ריקות זמינות לניהול ומוסתרות מהעמוד הציבורי.
			</p>
			{loading && <p role="status">טוענים…</p>}
			{error && (
				<Message role="alert">
					{error}
					<Action onClick={reload}>נסו שוב</Action>
				</Message>
			)}
			{feedback && <Message role="status">{feedback}</Message>}
			{failure && <Message role="alert">{failure}</Message>}
			<fieldset disabled={busy || loading || !!error}>
				<Columns>
					<div>
						<Panel>
							<h2>קטגוריות</h2>
							{!catalog.categories.length && (
								<p>אין עדיין קטגוריות. התחילו ביצירת קטגוריה.</p>
							)}
							{catalog.categories.map((c) => (
								<div key={c.id}>
									<strong>{c.name}</strong> (
									{catalog.services.filter((s) => s.categoryId === c.id).length}{' '}
									שירותים){' '}
									<Action onClick={() => setCategory(c)}>שינוי שם</Action>{' '}
									<Action
										onClick={() => {
											if (catalog.services.some((s) => s.categoryId === c.id)) {
												setFailure(
													'יש להעביר או למחוק את שירותי הקטגוריה לפני מחיקתה.'
												);

												return;
											}

											if (window.confirm(`למחוק את הקטגוריה ״${c.name}״?`))
												mutate(
													() =>
														axios.delete(
															`/services-management/categories/${c.id}`
														),
													() => setCategory({ id: 0, name: '' })
												);
										}}
									>
										מחיקה
									</Action>
								</div>
							))}
							<form
								onSubmit={(e) => {
									e.preventDefault();
									mutate(
										() =>
											category.id
												? axios.put(
														`/services-management/categories/${category.id}`,
														{ name: category.name }
												  )
												: axios.post('/services-management/categories', {
														name: category.name,
												  }),
										() => setCategory({ id: 0, name: '' })
									);
								}}
							>
								<label htmlFor="category-name">
									{category.id ? 'שינוי שם הקטגוריה' : 'קטגוריה חדשה'}
									<input
										id="category-name"
										required
										maxLength={120}
										value={category.name}
										onChange={(e) =>
											setCategory({ ...category, name: e.target.value })
										}
									/>
								</label>
								<Action type="submit">שמירת קטגוריה</Action>
								{!!category.id && (
									<Action
										type="button"
										onClick={() => setCategory({ id: 0, name: '' })}
									>
										ביטול
									</Action>
								)}
							</form>
						</Panel>
						<Panel>
							<h2>שירותים</h2>
							<Action
								onClick={() =>
									setDraft({
										...newService(),
										categoryId: catalog.categories[0]?.id || 0,
									})
								}
								disabled={!catalog.categories.length}
							>
								הוספת שירות
							</Action>
							{!catalog.services.length && <p>אין עדיין שירותים.</p>}
							{catalog.services.map((s) => (
								<div key={s.id}>
									<h3>{s.name}</h3>
									<p>
										{
											catalog.categories.find((c) => c.id === s.categoryId)
												?.name
										}{' '}
										· {startingPrice(s.priceCents)}
									</p>
									<Action
										onClick={() => setDraft(JSON.parse(JSON.stringify(s)))}
									>
										עריכה
									</Action>{' '}
									<Action onClick={() => deleteService(s)}>מחיקה</Action>
								</div>
							))}
						</Panel>
					</div>
					<div>
						{draft && (
							<Panel>
								<h2>{draft.id ? 'עריכת שירות' : 'שירות חדש'}</h2>
								<form key={draft.id} onSubmit={saveService}>
									<label htmlFor="service-name">
										שם השירות
										<input
											id="service-name"
											required
											maxLength={160}
											value={draft.name}
											onChange={(e) =>
												setDraft({ ...draft, name: e.target.value })
											}
										/>
									</label>
									<label htmlFor="service-description">
										מה מקבלים וכיצד השירות עוזר?
										<textarea
											id="service-description"
											required
											maxLength={5000}
											rows={4}
											value={draft.description}
											onChange={(e) =>
												setDraft({ ...draft, description: e.target.value })
											}
										/>
									</label>
									<label htmlFor="service-price">
										מחיר התחלתי בש״ח
										<MoneyInput
											id="service-price"
											cents={draft.priceCents}
											change={(priceCents) =>
												setDraft({ ...draft, priceCents })
											}
										/>
									</label>
									<label htmlFor="service-category">
										קטגוריה
										<select
											id="service-category"
											required
											value={draft.categoryId || ''}
											onChange={(e) =>
												setDraft({
													...draft,
													categoryId: Number(e.target.value),
												})
											}
										>
											<option value="" disabled>
												בחרו קטגוריה
											</option>
											{catalog.categories.map((c) => (
												<option key={c.id} value={c.id}>
													{c.name}
												</option>
											))}
										</select>
									</label>
									<label htmlFor="service-parent">
										שירות הורה - לתצוגה בלבד
										<select
											id="service-parent"
											value={draft.parentId || ''}
											onChange={(e) =>
												setDraft({
													...draft,
													parentId: Number(e.target.value) || null,
												})
											}
										>
											<option value="">ללא הורה</option>
											{catalog.services
												.filter((s) => s.id !== draft.id)
												.map((s) => (
													<option key={s.id} value={s.id}>
														{s.name}
													</option>
												))}
										</select>
									</label>
									<p>
										שיוך להורה מסדר את התצוגה בלבד. אפשר לבחור כל שירות בנפרד,
										והוא אינו נכלל אוטומטית.
									</p>
									<fieldset>
										<legend>שירותים הכלולים במחיר</legend>
										<p>
											בחירת שירות זה תכלול אוטומטית את השירותים שסומנו, גם דרך
											הכללות עקיפות, ללא חיוב נוסף.
										</p>
										<ServiceChoices
											catalog={catalog}
											except={draft.id}
											ids={draft.includedIds}
											change={(includedIds) =>
												setDraft({ ...draft, includedIds })
											}
										/>
									</fieldset>
									<fieldset>
										<legend>הנחות בעקבות בחירת השירות</legend>
										<p>
											כל כלל הוא סכום קבוע או אחוז בלבד. הנחה ייעודית קודמת
											להנחה כללית; נבחרת ההנחה הגבוהה ביותר ללא צבירה.
										</p>
										{draft.discounts.map((rule, index) => {
											const update = (next: Discount) =>
												setDraft({
													...draft,
													discounts: draft.discounts.map((d, i) =>
														i === index ? next : d
													),
												});

											return (
												<Panel key={index}>
													<label>
														סוג ההנחה
														<select
															value={rule.type}
															onChange={(e) =>
																update({
																	...rule,
																	type: e.target.value as Discount['type'],
																	value: 0,
																})
															}
														>
															<option value="fixed">סכום בש״ח</option>
															<option value="percentage">אחוז</option>
														</select>
													</label>
													<label>
														{rule.type === 'fixed'
															? 'סכום ההנחה בש״ח'
															: 'אחוז ההנחה'}
														<MoneyInput
															cents={rule.value}
															maximum={
																rule.type === 'percentage' ? 100 : 21474836.47
															}
															change={(value) => update({ ...rule, value })}
														/>
													</label>
													<label>
														על אילו שירותים חלה ההנחה?
														<select
															value={rule.allServices ? 'all' : 'specific'}
															onChange={(e) =>
																update({
																	...rule,
																	allServices: e.target.value === 'all',
																	targetIds: [],
																})
															}
														>
															<option value="all">כל השירותים</option>
															<option value="specific">שירותים מסוימים</option>
														</select>
													</label>
													{!rule.allServices && (
														<>
															<p>בחרו לפחות שירות אחד.</p>
															<ServiceChoices
																catalog={catalog}
																ids={rule.targetIds}
																change={(targetIds) =>
																	update({ ...rule, targetIds })
																}
															/>
														</>
													)}
													<Action
														type="button"
														onClick={() =>
															setDraft({
																...draft,
																discounts: draft.discounts.filter(
																	(_, i) => i !== index
																),
															})
														}
													>
														הסרת ההנחה
													</Action>
												</Panel>
											);
										})}
										<Action
											type="button"
											onClick={() =>
												setDraft({
													...draft,
													discounts: [
														...draft.discounts,
														{
															type: 'fixed',
															value: 0,
															allServices: true,
															targetIds: [],
														},
													],
												})
											}
										>
											הוספת הנחה
										</Action>
									</fieldset>
									<Action type="submit">שמירת שירות</Action>{' '}
									<Action type="button" onClick={() => setDraft(null)}>
										ביטול
									</Action>
								</form>
							</Panel>
						)}
					</div>
				</Columns>
			</fieldset>
			{busy && <p role="status">שומרים את השינויים…</p>}
		</Page>
	);
}

function ServiceChoices({
	catalog,
	except,
	ids,
	change,
}: {
	catalog: Catalog;
	except?: number;
	ids: number[];
	change: (ids: number[]) => void;
}) {
	return (
		<>
			{catalog.services
				.filter((s) => s.id !== except)
				.map((s) => (
					<Check key={s.id}>
						<input
							type="checkbox"
							checked={ids.includes(s.id)}
							onChange={() =>
								change(
									ids.includes(s.id)
										? ids.filter((id) => id !== s.id)
										: [...ids, s.id]
								)
							}
						/>
						{s.name}
					</Check>
				))}
		</>
	);
}

function MoneyInput({
	id,
	cents,
	change,
	maximum = 21474836.47,
}: {
	id?: string;
	cents: number;
	change: (value: number) => void;
	maximum?: number;
}) {
	const [raw, setRaw] = useState(String(cents / 100));
	const editing = useRef(false);
	useEffect(() => {
		if (!editing.current) setRaw(String(cents / 100));
	}, [cents]);

	return (
		<input
			id={id}
			type="number"
			required
			min="0"
			max={maximum}
			step="0.01"
			value={raw}
			onFocus={() => {
				editing.current = true;
			}}
			onBlur={() => {
				editing.current = false;
			}}
			onChange={(e) => {
				setRaw(e.target.value);
				change(moneyValue(e.target.value));
			}}
		/>
	);
}
